import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Skeleton from 'react-loading-skeleton';
import { Html } from 'cccisd-wysiwyg';
import ChartLegend from '../../ChartLegend';
import PieChart from 'js/vendor/reports/reportTemplates/components/PieChart';
import IconChart from 'cccisd-icons/pie-chart3';
import style from './style.css';

import _every from 'lodash/every';
import _round from 'lodash/round';
import _pick from 'lodash/pick';

const Component = props => {
    const reduxStore = useSelector(state => state.app.nasn);

    const { nationalData, nationalDataLoaded } = reduxStore;

    const nationalDataKey = props.filters.schoolType; // 'national_All Schools', 'national_Public Schools'

    const getReportedStatesCount = () => {
        if (nationalDataLoaded) {
            if (nationalData !== null && Object.keys(nationalData).includes(nationalDataKey)) {
                const data = nationalData[nationalDataKey];
                const hovData = _pick(data, [
                    'Proportion_RN_911',
                    'Proportion_RN_HOME',
                    'Proportion_RN_CLASS',
                    'Proportion_LPN_911',
                    'Proportion_LPN_HOME',
                    'Proportion_LPN_CLASS',
                    'Proportion_AIDE_911',
                    'Proportion_AIDE_HOME',
                    'Proportion_AIDE_CLASS',
                ]);

                if (Object.values(hovData).some(x => x !== '0') && data.Reported_HOV !== '0') {
                    return (
                        <>
                            {'('} {data.Reported_HOV} {' states represented )'}
                        </>
                    );
                }
                return '';
            }
        }
        return '';
    };

    const getData = () => {
        if (nationalDataLoaded) {
            if (nationalData !== null && Object.keys(nationalData).includes(nationalDataKey)) {
                const data = nationalData[nationalDataKey];
                return {
                    rn: [
                        {
                            id: '911',
                            label: '911',
                            value: _round(data.Proportion_RN_911 * 100, 2) || '',
                        },
                        {
                            id: 'home',
                            label: 'home',
                            value: _round(data.Proportion_RN_HOME * 100, 2) || '',
                        },
                        {
                            id: 'class',
                            label: 'class',
                            value: _round(data.Proportion_RN_CLASS * 100, 2) || '',
                        },
                    ],
                    lpn: [
                        {
                            id: '911',
                            label: '911',
                            value: _round(data.Proportion_LPN_911 * 100, 2) || '',
                        },
                        {
                            id: 'home',
                            label: 'home',
                            value: _round(data.Proportion_LPN_HOME * 100, 2) || '',
                        },
                        {
                            id: 'class',
                            label: 'class',
                            value: _round(data.Proportion_LPN_CLASS * 100, 2) || '',
                        },
                    ],
                    aide: [
                        {
                            id: '911',
                            label: '911',
                            value: _round(data.Proportion_AIDE_911 * 100, 2) || '',
                        },
                        {
                            id: 'home',
                            label: 'home',
                            value: _round(data.Proportion_AIDE_HOME * 100, 2) || '',
                        },
                        {
                            id: 'class',
                            label: 'class',
                            value: _round(data.Proportion_AIDE_CLASS * 100, 2) || '',
                        },
                    ],
                };
            }
        }

        return {
            rn: [
                {
                    id: '911',
                    label: '911',
                    value: '',
                },
                {
                    id: 'home',
                    label: 'home',
                    value: '',
                },
                {
                    id: 'class',
                    label: 'class',
                    value: '',
                },
            ],
            lpn: [
                {
                    id: '911',
                    label: '911',
                    value: '',
                },
                {
                    id: 'home',
                    label: 'home',
                    value: '',
                },
                {
                    id: 'class',
                    label: 'class',
                    value: '',
                },
            ],
            aide: [
                {
                    id: '911',
                    label: '911',
                    value: '',
                },
                {
                    id: 'home',
                    label: 'home',
                    value: '',
                },
                {
                    id: 'class',
                    label: 'class',
                    value: '',
                },
            ],
        };
    };

    const renderChartValues = domainData => {
        return (
            <div
                style={{
                    display: 'flex',
                }}
            >
                {domainData.map((d, i) => {
                    return (
                        <div
                            key={d.label}
                            style={{
                                background: ['#b2e2cd', '#fdcdab', '#cbd6e7'][i],
                                flexGrow: 1,
                                padding: '0 5px',
                                textAlign: 'center',
                            }}
                        >
                            <span
                                style={{
                                    fontWeight: 'bold',
                                    color: '#333',
                                }}
                            >
                                {d.value ? d.value + '%' : 0 + '%'}
                            </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <>
            {props.settings.show && (
                <div className={style.wrapper}>
                    <div className={style.header}>
                        <div className={style.headerLeft}>
                            <div className={style.headerIcon} />
                            {props.settings.title || 'Health Office Visits'}
                        </div>
                        <div className={style.stateInfo}>{getReportedStatesCount()}</div>
                    </div>
                    <div className={style.content}>
                        {!nationalDataLoaded ? (
                            <Skeleton height={268} />
                        ) : (
                            <>
                                <ChartLegend
                                    items={[
                                        { label: 'Sent home', color: '#fdcdab' },
                                        { label: 'Called 911', color: '#b2e2cd' },
                                        { label: 'Sent back to class', color: '#cbd6e7' },
                                    ]}
                                />
                                <div className={style.chartWrapper}>
                                    <div className={style.box}>
                                        <div className={style.boxHeading}>RN</div>
                                        {_every(getData().rn, ['value', '']) ? (
                                            <div className={style.chartPlaceholder}>
                                                <IconChart />
                                            </div>
                                        ) : (
                                            <>
                                                <PieChart data={getData().rn} />
                                                {renderChartValues(getData().rn)}
                                            </>
                                        )}
                                    </div>
                                    <div className={style.box}>
                                        <div className={style.boxHeading}>LPN</div>
                                        {_every(getData().lpn, ['value', '']) ? (
                                            <div className={style.chartPlaceholder}>
                                                <IconChart />
                                            </div>
                                        ) : (
                                            <>
                                                <PieChart data={getData().lpn} />
                                                {renderChartValues(getData().lpn)}
                                            </>
                                        )}
                                    </div>
                                    <div className={style.box}>
                                        <div className={style.boxHeading}>AIDE</div>
                                        {_every(getData().aide, ['value', '']) ? (
                                            <div className={style.chartPlaceholder}>
                                                <IconChart />
                                            </div>
                                        ) : (
                                            <>
                                                <PieChart data={getData().aide} />
                                                {renderChartValues(getData().aide)}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                        <div className={style.additionalInfo}>
                            <Html content={props.settings.additionalInfo} />
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

Component.propTypes = {
    settings: PropTypes.object,
    isPreview: PropTypes.bool,
    filters: PropTypes.object,
};

Component.defaultProps = {
    filters: {},
};

export default Component;
